export function nowIso(): string {
  return new Date().toISOString();
}

export function cleanText(value: unknown, maxLength = 220): string | undefined {
  if (typeof value !== "string") return undefined;
  const cleaned = value
    .replace(/\s+/g, " ")
    .replace(/[{}[\]"]/g, "")
    .trim();
  if (cleaned.length === 0) return undefined;
  if (cleaned.length <= maxLength) return cleaned;
  // Prefer a complete sentence. Besides sounding unfinished, a word-only cut
  // can leave a numbered answer ending in a bare marker such as "5.". Ignore
  // those markers when looking for sentence punctuation.
  const sliced = cleaned.slice(0, maxLength);
  const sentenceBoundaries = Array.from(sliced.matchAll(/[.!?](?=\s|$)/g))
    .map(match => (match.index ?? -1) + 1)
    .filter(index => {
      if (index < maxLength * 0.6) return false;
      return !/(?:^|\s)\d+\.$/u.test(sliced.slice(0, index));
    });
  const lastSentenceBoundary = sentenceBoundaries.at(-1);
  if (lastSentenceBoundary) return sliced.slice(0, lastSentenceBoundary);

  // Fall back to a word boundary for short labels and prose without sentence
  // punctuation; a hard slice leaves user-visible text cut mid-word.
  const lastSpace = sliced.lastIndexOf(" ");
  return lastSpace > maxLength * 0.6 ? sliced.slice(0, lastSpace) : sliced;
}

export function normalize(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

export function listFromValue(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.flatMap(listFromValue);
  }
  if (typeof value !== "string") return [];
  return value
    .split(/\s*(?:,|\/|\band\b|\+)\s*/i)
    .map(part => part.trim())
    .filter(Boolean);
}

export function compactValue(value: unknown): string | undefined {
  if (Array.isArray(value)) {
    const joined = value
      .filter((item): item is string => typeof item === "string")
      .map(item => item.trim())
      .filter(Boolean)
      .join(", ");
    return joined || undefined;
  }
  return cleanText(value, 320);
}

/**
 * Like compactValue but for user-facing message/answer text authored by the
 * planner. The 320-char compactValue cap is meant for structured field values;
 * applying it to replies truncates real answers mid-sentence (found live when a
 * fit-advice answer was cut at "1. **A C").
 */
export function compactMessage(
  value: unknown,
  maxLength = 1200
): string | undefined {
  if (typeof value !== "string") return undefined;
  return cleanText(value, maxLength);
}

export function unique(values: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    const key = normalize(value);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    result.push(value.trim());
  }
  return result;
}

export function mergeUnique(
  existing: string[] | undefined,
  next: string[]
): string[] {
  return unique([...(existing ?? []), ...next]);
}

export function shortLabel(value: unknown): string | undefined {
  const text = compactValue(value);
  if (!text) return undefined;
  return text
    .split(" - ")[0]
    .replace(/\s*\/\s*Column\b/i, "")
    .trim();
}

/**
 * Lowercase short label with a category word appended ("floor" → "floor
 * length"), skipping the suffix when the value already ends in it so a
 * free-text field like "floor length" does not read as "floor length length".
 */
export function labelWithSuffix(
  value: unknown,
  suffix: string,
  options: { preserveCase?: boolean } = {}
): string | undefined {
  const short = shortLabel(value);
  if (!short) return undefined;
  const label = options.preserveCase ? short : short.toLowerCase();
  const namesCategory =
    short.toLowerCase().endsWith(suffix.toLowerCase()) ||
    (suffix === "sleeves" &&
      /\b(?:sleeves?|sleeveless|strapless)\b/i.test(short));
  return namesCategory ? label : `${label} ${suffix}`;
}

export function formatList(values: string[]): string {
  if (values.length <= 1) return values[0] ?? "";
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(", ")}, and ${values[values.length - 1]}`;
}
